import { Avatar, BellIcon, Box, Card, Icon } from '@deckee/deck-hand';
import React, { FC } from 'react';
import styled, { useTheme } from 'styled-components';
import IconWithTextLockup from '../../../lockups/IconWithTextLockup/IconWithTextLockup';
import { ReactSvg, threeDots } from '../../../shared/vector-images';
import useShortenedRelativeTime from '../helpers/shortened-relative-time';

interface NotificationItemProps {
  title: string;
  read: boolean;
  timestamp?: string;
  description?: string | undefined;
  icon?: Promise<Record<string, unknown>>;
  iconType?: 'prioritized' | 'weather' | 'alert' | 'notice' | undefined;
  iconUrl?: string;
  imagePath?: string;
  onClick?: () => void;
  onMorePress?: () => void;
  className?: string;
}

const StyledCard = styled(Card)`
  cursor: pointer;
  border-style: solid;
  border-width: 0.5px;
  border-color: #dfdfdf;
  background: ${({ theme }) => theme.colors.secondarylight};

  ${({ read, theme }) =>
    read &&
    `
    background:  ${theme.colors.white};
  `}
`;

const MoreButton = styled(Box)`
  cursor: pointer;
  padding: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotificationItem: FC<NotificationItemProps> = ({
  title,
  read,
  icon,
  iconType,
  iconUrl,
  imagePath,
  timestamp,
  description,
  onClick,
  onMorePress,
  className,
}) => {
  const relativeTime = useShortenedRelativeTime(timestamp || '');

  const theme = useTheme();

  const IconOrImageToRender = () => {
    if (icon && iconType === 'weather') {
      return (
        <Box bg="white" borderRadius="50%" width="38px" height="38px">
          <ReactSvg
            toDisplay={icon}
            options={{
              fillColor: theme.colors.danger,
            }}
            width={38}
            height={38}
          />
        </Box>
      );
    }
    if (icon && iconType === 'prioritized') {
      return (
        <Box bg="white" borderRadius="50%" width="38px" height="38px">
          <ReactSvg
            toDisplay={icon}
            options={{
              fillColor: theme.colors.white,
              circleColor: theme.colors.danger,
            }}
            width={38}
            height={38}
          />
        </Box>
      );
    }
    if (icon && iconType === 'alert') {
      return (
        <Box bg="white" borderRadius="50%" width="38px" height="38px">
          <ReactSvg
            toDisplay={icon}
            options={{
              fillColor: theme.colors.white,
              circleColor: theme.colors.warning,
            }}
            width={38}
            height={38}
          />
        </Box>
      );
    }
    if (icon && iconType === 'notice') {
      return (
        <Box bg="white" borderRadius="50%" width="38px" height="38px">
          <ReactSvg
            toDisplay={icon}
            options={{
              fillColor: theme.colors.white,
              circleColor: '#139ECA',
            }}
            width={38}
            height={38}
          />
        </Box>
      );
    }
    if (imagePath || iconUrl) {
      return <Avatar src={imagePath || iconUrl} />;
    }
    return (
      <Icon.Circle>
        <BellIcon color={theme.colors.darkergrey} />
      </Icon.Circle>
    );
  };

  const handleSelect = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleMorePress = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMorePress) {
      onMorePress();
    }
  };

  return (
    <StyledCard read={read} className={className}>
      <Card.Body p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box flex="1" onClick={handleSelect}>
            <IconWithTextLockup
              image={IconOrImageToRender()}
              heading={title}
              description={
                <>
                  {description && <Box>{description}</Box>}
                  {relativeTime && <Box>{relativeTime}</Box>}
                </>
              }
            />
          </Box>
          {onMorePress && (
            <MoreButton
              onClick={handleMorePress}
              aria-label="Notification options"
              role="button"
            >
              <ReactSvg
                toDisplay={threeDots()}
                options={{
                  fillColor: theme.colors.primary,
                }}
                width={24}
                height={24}
              />
            </MoreButton>
          )}
        </Box>
      </Card.Body>
    </StyledCard>
  );
};

NotificationItem.defaultProps = {
  onClick: undefined,
  onMorePress: undefined,
  timestamp: undefined,
  description: undefined,
  icon: undefined,
  iconType: 'weather',
  iconUrl: undefined,
  imagePath: undefined,
  className: undefined,
};

export default NotificationItem;
